import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Helmet from 'react-helmet';
import _isEmpty from 'lodash/isEmpty';
import { getDefaultProductImage } from 'yoda-site-components/lib/selectors/ProductDetailsSelector';
import { constructSEODefaultCanonicalUrl } from 'yoda-site-components/lib/helpers/Utils/seoHelper';

let wwwHost;

// For updating schema.offers price details on page load alone
let onPageLoad = true;
let priceInfo = [];

/**
 * This method get all the skus for a web id.
 * @param {Array} items         Lot items
 * @param {Object} priceObject  Price object from lotPrice
 * @return {Object}
 */
const getSKUs = (items, priceObject) => {
    const skusObject = [];
    items.forEach((item) => {
        skusObject.push({ ...priceObject, ...{ sku: item.id } });
    });
    return skusObject;
};

/**
 * This method buils the skuObject for schema.
 * @param {Object} priceObject  Price object from lotPrice
 * @param {Array} lots          Product lots from productDetails
 * @return {Array}
 */
const buildSKUObject = (priceObject, lots) => {
    let returnObj = [];
    /* istanbul ignore else */
    if (lots.length > 0 && priceObject) {
        lots.forEach((lot) => {
            if (lot.id === priceObject.id) {
                returnObj = getSKUs(lot.items, priceObject);
            }
        });
    }
    return returnObj;
};

/**
 * This method generates the price data for schema
 * @param {Object} lotPrice     Lot price object from productDetails
 * @param {Array} lots          Product lots from productDetails
 * @return {Array}
 */

const getPriceDataFromLotPrice = (lotPrice = {}, lots) => {
    /* istanbul ignore next */
    if (onPageLoad && lotPrice.amounts) {
        const { couponInfo, amounts = [], eligibleCouponInfo, id = '' } = lotPrice;
        const couponPriceInfo = couponInfo || eligibleCouponInfo || [];
        let price;
        if (couponPriceInfo[0]) {
            price = { ...couponPriceInfo[0].amount, ...{ id } };
        } else if (amounts.length) {
            amounts.forEach((amount, idx) => {
                if (
                    idx === 0 ||
                    (amount.type &&
                        (amount.type.toUpperCase() === 'CLEARANCE' ||
                            amount.type.toUpperCase() === 'SALE'))
                ) {
                    price = { ...amount, ...{ id } };
                }
            });
        }
        priceInfo = priceInfo.concat(buildSKUObject(price, lots));
        onPageLoad = false;
    }
    return priceInfo;
};

const preconnects = [
    '//fonts.gstatic.com',
    '//browse-api.jcpenney.com',
    '//search-api.jcpenney.com',
    '//api.jcpapi.com',
    '//code.jquery.com',
    '//d2bhklarlhhnvo.cloudfront.net',
    '//gateway.answercloud.com',
    '//netvision.cavisson.com',
    '//login.ditomi.com',
    '//s.yimg.com',
    '//googleads.g.doubleclick.net',
    '//googletagservices.com',
    '//api.jcpclick.com',
    '//assets.jetlore.com',
];

export default class Metadata extends Component {
    static defaultProps = {
        productDetails: {},
        location: {},
        httpsCanonical: false,
        lotPriceInventory: {},
        preferences: {},
    };

    static propTypes = {
        productDetails: PropTypes.oneOfType([PropTypes.object]),
        httpsCanonical: PropTypes.bool,
        lotPriceInventory: PropTypes.oneOfType([PropTypes.object]),
        preferences: PropTypes.oneOfType([PropTypes.object]),
    };

    /* istanbul ignore next */
    constructor(props) {
        super(props);
        if (!wwwHost) {
            const protocol = this.props.httpsCanonical ? 'https' : 'http';
            wwwHost = `${protocol}://www.jcpenney.com`;
        }
    }

    buildSchema(name, image, description, brandName, productID) {
        const { productDetails, lotPriceInventory } = this.props;
        const lots = productDetails.lots || [];
        const valuation = productDetails.valuation || {};
        const priceData = onPageLoad
            ? getPriceDataFromLotPrice(lotPriceInventory, lots)
            : priceInfo;
        const schema = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            brand: {
                '@type': 'Thing',
                name: brandName,
            },
            name,
            image,
            description,
            productID,
        };

        if (priceData.length) {
            const offers = [];
            priceData.forEach((price) => {
                offers.push({
                    '@type': 'Offer',
                    priceCurrency: 'USD',
                    price: price.min,
                    availability: 'https://schema.org/InStock',
                    sku: price.sku,
                    itemCondition: 'https://schema.org/NewCondition',
                    seller: {
                        '@type': 'Organization',
                        name: 'JCPenney',
                    },
                });
            });
            schema.offers = offers;
        }

        if (valuation.rating && valuation.reviews) {
            schema.aggregateRating = {
                '@type': 'AggregateRating',
                ratingValue: valuation.rating,
                reviewCount: valuation.reviews.count,
            };
        }

        return (
            // need to set this with dangerouslySetInnerHTML, otherwise the content
            // gets encoded.
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            /> // eslint-disable-line react/no-danger
        );
    }

    renderPreconnect() {
        return preconnects.map((url) => (
            <link href={url} key={url} rel="preconnect" crossOrigin="crossorigin" />
        )); // eslint-disable-line react/no-unknown-property
    }

    render() {
        const {
            productDetails,
            preferences: { seoConfig: { excludeQueryParams = [], includeQueryParams = [] } = {} },
        } = this.props;
        const { name, id } = productDetails;
        const meta = productDetails.meta || {};
        const tags =
            Array.isArray(productDetails.lots) && productDetails.lots[0].tags
                ? productDetails.lots[0].tags
                : [];
        const isNoIndex = tags.indexOf('no-index') !== -1;
        const imageUrl = getDefaultProductImage(productDetails).url || '';
        const brandName = productDetails.brand ? productDetails.brand.name : '';
        let canonicalUrl = !_isEmpty(meta.canonicalUrl) ? `${wwwHost}${meta.canonicalUrl}` : '';
        // let canonicalUrl = '';
        if (_isEmpty(canonicalUrl)) {
            canonicalUrl = constructSEODefaultCanonicalUrl(
                !__SERVER__ && window ? window.location.href : '',
                excludeQueryParams,
                includeQueryParams,
                false
            );
        }
        return (
            <div className="metadata-wrapper">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>{meta.title}</title>
                    <meta name="description" content={meta.description} />
                    {meta && meta.keyword ? <meta name="keywords" content={meta.keyword} /> : null}

                    {/* Twitter MetaData */}
                    {meta && meta.twitter && meta.twitter.card ? (
                        <meta name="twitter:card" content={meta.twitter.card} />
                    ) : null}
                    {meta && meta.twitter && meta.twitter.site ? (
                        <meta name="twitter:site" content={meta.twitter.site} />
                    ) : null}

                    {/* Facebook MetaData */}
                    {meta && meta.openGraph && meta.openGraph.title ? (
                        <meta property="og:title" content={meta.openGraph.title} />
                    ) : null}
                    {meta && meta.openGraph && meta.openGraph.description ? (
                        <meta property="og:description" content={meta.openGraph.description} />
                    ) : null}
                    {meta && meta.openGraph && meta.openGraph.sitename ? (
                        <meta property="og:site_name" content={meta.openGraph.sitename} />
                    ) : null}
                    {meta && meta.openGraph && meta.openGraph.type ? (
                        <meta property="og:type" content={meta.openGraph.type} />
                    ) : null}
                    {meta && meta.openGraph && meta.openGraph.image ? (
                        <meta property="og:image" content={meta.openGraph.image} />
                    ) : null}
                    {meta && meta.openGraph && meta.openGraph.url ? (
                        <meta property="og:url" content={meta.openGraph.url} />
                    ) : null}

                    {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

                    {/* Add noindex,nofollow for tagged pages */}
                    {isNoIndex ? <meta name="robots" content="noindex, nofollow" /> : null}
                    {this.renderPreconnect()}
                </Helmet>
                {this.buildSchema(name, imageUrl, meta.description, brandName, id)}
            </div>
        );
    }
}
